import { FC, useEffect, useState } from 'react';
import axios from 'axios';
import { useAppSelector } from '../../store';
import { baseUrl } from '../../hooks/useEndpoints';
import CustomButton from '../CustomButton/CustomButton';
import styles from './PrivacyConsentModal.module.scss';

interface ConsentStatus {
  privacyPolicy: {
    currentVersion: string;
    acceptedVersion: string | null;
    acceptedAt: string | null;
    needsReConsent: boolean;
  };
  healthData: { accepted: boolean; acceptedAt: string | null };
}

/**
 * Step E + 9 — Modal blocco mostrato all'utente loggato finché non
 * accetta la versione corrente della Privacy Policy + consent
 * processing dati salute (categoria speciale art. 9 GDPR).
 *
 * Logica:
 * 1. Al mount, GET /users/:id/gdpr/consent-status
 * 2. Se needsReConsent || !healthData.accepted → mostra modal
 * 3. Utente click "Accetto" → POST /users/:id/gdpr/consent
 * 4. On success, rimuove modal
 */
export const PrivacyConsentModal: FC = () => {
  const selectedEntity = useAppSelector((st) => st.me.selectedEntity);
  const userId = (selectedEntity as any)?._id as string | undefined;
  const userType = (selectedEntity as any)?.type as string | undefined;

  const [status, setStatus] = useState<ConsentStatus | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [acceptHealth, setAcceptHealth] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    // Solo per atleti il consent dati salute è rilevante
    axios
      .get(`${baseUrl}/users/${userId}/gdpr/consent-status`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  }, [userId]);

  const needsReConsent = status?.privacyPolicy.needsReConsent;
  const needsHealthConsent =
    userType === 'athlete' && status?.healthData.accepted === false;
  const shouldShow = !!status && (needsReConsent || needsHealthConsent);

  if (!shouldShow || !userId) return null;

  const handleAccept = async () => {
    setSubmitting(true);
    setErrore(null);
    try {
      await axios.post(`${baseUrl}/users/${userId}/gdpr/consent`, {
        privacyPolicy: needsReConsent === true,
        healthData: needsHealthConsent ? acceptHealth : false,
      });
      setStatus(null); // chiudi modal
    } catch (err: any) {
      /**
       * L'errore va detto per quello che è: un `alert()` con "Riprova fra
       * qualche minuto" ha mandato in assistenza chi era bloccato da un 429
       * (quota condivisa fra tutti gli utenti) e chi non aveva i permessi,
       * senza modo di distinguere i due casi.
       */
      const stato = err?.response?.status;
      if (stato === 429) {
        setErrore(
          'Troppi tentativi ravvicinati. Aspetta qualche minuto e riprova.',
        );
      } else if (stato === 403) {
        setErrore(
          'Il tuo profilo non ha i permessi per registrare questo consenso. Scrivi a privacy@leopartmove.com.',
        );
      } else {
        setErrore(
          'Non siamo riusciti a registrare il consenso. Riprova; se continua, scrivi a privacy@leopartmove.com.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <h2 className={styles.title}>Privacy Policy aggiornata</h2>
        <p className={styles.text}>
          Per continuare a usare LeoPart abbiamo bisogno del tuo consenso
          aggiornato. Leggi i punti qui sotto e accetta per proseguire.
        </p>

        {needsReConsent && (
          <div className={styles.section}>
            <strong>Privacy Policy</strong>
            <p>
              Versione corrente:{' '}
              <code>{status!.privacyPolicy.currentVersion}</code>.{' '}
              {status!.privacyPolicy.acceptedVersion ? (
                <>
                  Hai accettato la versione{' '}
                  <code>{status!.privacyPolicy.acceptedVersion}</code>. Ti
                  chiediamo di accettare l&apos;aggiornamento.
                </>
              ) : (
                <>Non hai mai accettato una versione precedente.</>
              )}{' '}
              <a href="/privacy" target="_blank" rel="noreferrer">
                Leggi Privacy Policy
              </a>
              .
            </p>
          </div>
        )}

        {needsHealthConsent && (
          <div className={styles.section}>
            <strong>Consenso dati salute (art. 9 GDPR)</strong>
            <p>
              LeoPart raccoglie dati di salute (questionari Hooper su
              sonno/fatica/stress, metriche FCR/HRV, BodyMap dolori,
              recovery) per calcolare il tuo readiness e ottimizzare i
              tuoi allenamenti. Questi dati appartengono alla{' '}
              <em>categoria speciale</em> (art. 9 GDPR) e richiedono il
              tuo consenso esplicito separato.
            </p>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={acceptHealth}
                onChange={(e) => setAcceptHealth(e.target.checked)}
              />
              <span>
                Acconsento al trattamento dei miei dati di salute per
                le finalità descritte. Posso revocare il consenso in
                qualsiasi momento dalle impostazioni del profilo o
                via email a privacy@leopartmove.com.
              </span>
            </label>
          </div>
        )}

        {errore && (
          <p className={styles.errore} role="alert">
            {errore}
          </p>
        )}

        <div className={styles.actions}>
          <CustomButton color="outline" onClick={() => window.location.assign('/login')}>
            Esci
          </CustomButton>
          <CustomButton
            color="primary"
            isLoading={submitting}
            disabled={
              submitting ||
              (needsHealthConsent === true && acceptHealth === false)
            }
            onClick={handleAccept}
          >
            Accetto
          </CustomButton>
        </div>
      </div>
    </div>
  );
};
